import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import h1 from "@/assets/hero/hero-1-enseigne.jpg";
import h2 from "@/assets/hero/hero-2-dossier.jpg";
import h3 from "@/assets/hero/hero-3-social.jpg";
import h4 from "@/assets/hero/hero-4-packaging.jpg";
import h5 from "@/assets/hero/hero-5-web.jpg";
import h6 from "@/assets/hero/hero-6-logo.jpg";

const SLIDES = [
  { src: h1, key: "enseigne" },
  { src: h2, key: "dossier" },
  { src: h3, key: "social" },
  { src: h4, key: "packaging" },
  { src: h5, key: "web" },
  { src: h6, key: "logo" },
] as const;

const INTERVAL = 4500;

export const HeroCarousel = () => {
  const { t } = useTranslation("landing");
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), INTERVAL);
    return () => clearInterval(id);
  }, []);

  const current = SLIDES[index];
  const label = t(`hero_carousel.slides.${current.key}`, { defaultValue: "" });

  return (
    <div className="card-warm relative z-10 overflow-hidden p-2.5 sm:p-4">
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl bg-muted">
        <AnimatePresence mode="sync">
          <motion.img
            key={current.key}
            src={current.src}
            alt={label || "Conceptions.ma showcase"}
            width={1216}
            height={896}
            loading={index === 0 ? "eager" : "lazy"}
            fetchPriority={index === 0 ? "high" : "auto"}
            decoding={index === 0 ? "sync" : "async"}
            initial={index === 0 ? false : { opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 size-full object-cover"
          />
        </AnimatePresence>

        {/* Caption */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent p-3 sm:p-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.key}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.4 }}
              className="flex items-center justify-between gap-3"
            >
              <span className="rounded-full bg-white/90 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-foreground sm:text-xs">
                {label}
              </span>
              <span className="text-[10px] font-semibold text-white/90 sm:text-xs">
                {String(index + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
              </span>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Dots */}
      <div className="mt-3 flex items-center justify-center gap-1.5" role="tablist" aria-label="Sélecteur de visuel">
        {SLIDES.map((s, i) => {
          const slideLabel = t(`hero_carousel.slides.${s.key}`, { defaultValue: s.key });
          return (
            <button
              key={s.key}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`Afficher le visuel ${i + 1} sur ${SLIDES.length} : ${slideLabel}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "w-6 bg-primary" : "w-1.5 bg-foreground/20 hover:bg-foreground/40"
              }`}
            />
          );
        })}
      </div>
    </div>
  );
};
